<template>
  <div id="app">
    <!-- 全局加载状态 -->
    <transition name="fade" mode="out-in">
      <div v-if="isLoading" class="global-loading">
        <div class="loading-spinner"></div>
      </div>
    </transition>
    <!-- 路由视图 -->
    <router-view v-slot="{ Component, route }">
      <transition name="page" mode="out-in">
        <keep-alive :include="cachedComponents">
          <component :is="Component" :key="route.path" />
        </keep-alive>
      </transition>
    </router-view>
    <!-- 全局错误提示 -->
    <div v-if="globalError" class="global-error">
      <p>{{ globalError }}</p>
      <button @click="clearError">关闭</button>
    </div>
  </div>
</template>

<script>
import channelMaterial from '@/utils/channelMaterial'

export default {
  name: 'App',

  data() {
    return {
      isLoading: false,
      globalError: null,
      cachedComponents: ['Home']
    }
  },

  created() {
    this.initializeApp()
  },

  mounted() {
    this.setupGlobalHandlers()
  },

  beforeUnmount() {
    this.cleanup()
  },

  methods: {
    initializeApp() {
      channelMaterial.setPageMeta()
      this.setupGlobalConfig()
      this.$router.beforeEach((to, from, next) => {
        this.isLoading = true
        next()
      })
      this.$router.afterEach(() => {
        this.isLoading = false
      })
    },

    setupGlobalConfig() {
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection)
      window.addEventListener('error', this.handleGlobalError)
    },

    setupGlobalHandlers() {
      window.addEventListener('online', this.handleOnline)
      window.addEventListener('offline', this.handleOffline)
    },

    handleUnhandledRejection(event) {
      this.showGlobalError('网络请求失败，请稍后重试')
      event.preventDefault()
    },

    handleGlobalError(event) {
      console.error('全局错误:', event.error)
      this.showGlobalError('应用出现错误，请刷新页面重试')
    },

    showGlobalError(message) {
      this.globalError = message
      setTimeout(() => {
        this.clearError()
      }, 5000)
    },

    clearError() {
      this.globalError = null
    },

    handleOnline() {
      this.$message?.success('网络连接已恢复')
    },

    handleOffline() {
      this.$message?.warning('网络连接已断开')
    },

    cleanup() {
      window.removeEventListener('unhandledrejection', this.handleUnhandledRejection)
      window.removeEventListener('error', this.handleGlobalError)
      window.removeEventListener('online', this.handleOnline)
      window.removeEventListener('offline', this.handleOffline)
    },

    handleClose(done) {
      done()
    }
  }
}
</script>

<style>
/* ===== CSS 自定义属性（CSS Variables）===== */
:root {
  --primary-color: #409eff;
  --success-color: #67c23a;
  --warning-color: #e6a23c;
  --danger-color: #f56c6c;
  --info-color: #909399;

  --text-primary: #303133;
  --text-regular: #606266;
  --text-secondary: #909399;
  --text-placeholder: #c0c4cc;

  --border-base: #dcdfe6;
  --border-light: #e4e7ed;
  --border-lighter: #ebeef5;
  --border-extra-light: #f2f6fc;

  --bg-color: #ffffff;
  --bg-color-page: #f2f3f5;
  --bg-color-light: #fcfcfc;

  --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
  --font-family-mono: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;

  --box-shadow-base: 0 2px 4px rgba(0, 0, 0, 0.12), 0 0 6px rgba(0, 0, 0, 0.04);
  --box-shadow-light: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

  --transition-base: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  --transition-fast: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}

/* ===== 现代化 CSS Reset ===== */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ===== 全局基础 ===== */
html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  font: 12px / 20px "微软雅黑", "Microsoft YaHei", "宋体", "Arial";
  color: var(--text-primary);
  background-color: var(--bg-color-page);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/* ===== 应用根容器 ===== */
#app {
  width: 100%;
}

/* ===== 覆盖 wangeditor 注入到 <head> 的全局污染 ===== */
/* wangeditor 会 inject 一段 `* { margin:0; padding:0; box-sizing:border-box }`
   和 `html, body, #app { overflow:hidden }`，影响整个页面。
   此处用更高 specificity 还原页面正常状态。 */
body {
  margin: 0;
}
html,
body {
  overflow: auto !important;
  height: auto;
  min-height: 100%;
}

/* ===== 全局加载状态 ===== */
.global-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-lighter);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ===== 全局错误提示 ===== */
.global-error {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--danger-color);
  color: #fff;
  padding: 12px 24px;
  border-radius: 4px;
  z-index: 99999;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.global-error p {
  margin: 0;
}

.global-error button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #fff;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.global-error button:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* ===== 页面切换动画 ===== */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ===== 全局滚动条美化 ===== */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* ===== 禁止选中文本 ===== */
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* ===== 通用辅助类 ===== */
.text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== Element UI 全局覆盖 ===== */
.el-message {
  z-index: 99999 !important;
}

.el-notification {
  z-index: 99999 !important;
}

.el-dialog {
  z-index: 9999 !important;
}

.el-loading-mask {
  z-index: 9998 !important;
}

/* ===== 响应式断点 ===== */
@media (max-width: 768px) {
  body {
    font-size: 14px;
  }
}
</style>
